import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Row, Stack } from "react-bootstrap";
import Input from "../../ui/Input";
import { unsavedChanges } from "../../../App";
import { deleteNoteTypeField, patchNoteTypeField } from "../../../services/httpClient";
import SelectObject from "../../ui/SelectObject";
import Textarea from "../../ui/Textarea";
import FieldChoices from "./FieldChoices";
import IconButton from "../../ui/IconButton";
import { faEdit, faEye, faEyeSlash, faTrash } from "@fortawesome/free-solid-svg-icons";
import fieldTypes from "../../../data/notetypefieldtypes.json";

interface FieldType {
  key: string;
  name: string;
}
interface FieldState {
  typeId: any;
  field: any;
  lastField: boolean;
  getNotes: any;
  getNoteTypeFields: any;
  archived: boolean | number | null | undefined;
  published: boolean | number | null | undefined;
  setOrgToSave: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  setErrMessage: any;
  numPreviews: number;
  setNumPreviews: any;
}
const NoteTypeCardField = ({
  typeId,
  field,
  lastField,
  getNotes,
  getNoteTypeFields,
  archived,
  published,
  setOrgToSave,
  setShowToast,
  setToastText,
  setToastBody,
  setErrMessage,
  numPreviews,
  setNumPreviews,
}: FieldState) => {
  const [fieldLabel, setFieldLabel] = useState<string>(field?.label);
  const [options, setOptions] = useState<Array<string | { id?: any; name: string; key?: any; order?: any }>>(field?.options || []);
  const [fieldType, setFieldType] = useState<string>(field?.type);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [allowSave, setAllowSave] = useState<boolean>(false);
  const [previewEnabled, setPreviewEnabled] = useState<boolean>(!!field?.preview) || false;
  const typesList: FieldType[] = fieldTypes;

  useEffect(() => {
    getNoteTypeFields();
  }, []);

  const updateLabel = (e: any) => {
    e.stopPropagation();
    setFieldLabel(e.target.value);
    setAllowSave(true);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const updateType = (e: any) => {
    e.stopPropagation();
    setFieldType(e.target.value);
    setAllowSave(true);
    unsavedChanges.value = true;
    setOrgToSave(true);
    if (e.target.value === "dropdown") {
      setOptions([]);
    }
  };

  const togglePreview = (e?: any) => {
    e?.stopPropagation();
    const newPreview = !previewEnabled;
    //prevent adding more than 2 previews
    if (numPreviews >= 2 && newPreview) return;

    setNumPreviews((prev: number) => {
      const current = typeof prev === "number" ? prev : numPreviews || 0;
      return newPreview ? current + 1 : Math.max(0, current - 1);
    });
    // if more than 2 previews added, cancel
    if (numPreviews > 2) {
      setNumPreviews(2);
      setPreviewEnabled(false);
      return;
    }
    setPreviewEnabled(newPreview);

    patchNoteTypeField({ preview: newPreview }, typeId, field?.id)
      .then((res: any) => {
        setShowToast(true);
        setToastText(newPreview ? "Field added to preview" : "Field removed from preview");
        setToastBody(fieldLabel);
      })
      .catch((err: any) => {
        setPreviewEnabled(!newPreview);
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const onClickEdit = (e: any) => {
    e.stopPropagation();
    setIsEditing(true);
    // If editing a dropdown, ensure options are loaded from field
    if (fieldType === "dropdown" && Array.isArray(field?.options)) {
      setOptions(field.options);
    }
  };

  const onClickSaveFieldSubmit = (e: any) => {
    e?.preventDefault();
    onClickSaveField();
  };

  const onClickSaveField = () => {
    const updatedField: any = {
      label: fieldLabel,
      type: fieldType,
    };

    setErrMessage("");
    patchNoteTypeField(updatedField, typeId, field?.id)
      .then((res: any) => {
        setIsEditing(false);
        setAllowSave(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        getNotes();
        getNoteTypeFields();
        setShowToast(true);
        setToastText("Note Type Field Updated");
        setToastBody(fieldLabel);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const onClickCancel = (e: any) => {
    setErrMessage("");
    e.stopPropagation();
    setFieldLabel(field?.label);
    setFieldType(field?.type);
    setAllowSave(false);
    setIsEditing(false);
    unsavedChanges.value = false;
    setOrgToSave(false);
  };

  const onClickDelete = () => {
    setIsDeleting(true);
  };

  const onClickDeleteFinal = () => {
    deleteNoteTypeField(typeId, field?.id)
      .then((res: any) => {
        setIsDeleting(false);
        getNotes();
        getNoteTypeFields();
        setShowToast(true);
        setToastText("Note Type Field Deleted");
        setToastBody(fieldLabel);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
  };

  const renderDelete = () => {
    return (
      <Row className="my-3">
        <Stack direction="horizontal" gap={3}>
          <Stack style={{ justifyContent: "center" }} className="col-6">
            <Card.Subtitle>Are you sure you want to delete {fieldLabel} field?</Card.Subtitle>
          </Stack>
          <Button className="app-danger-color button" onClick={() => onClickDeleteFinal()}>
            Confirm Delete
          </Button>
          <div className="vr" />
          <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
            Cancel
          </Button>
        </Stack>
      </Row>
    );
  };

  return (
    <>
      {isDeleting ? (
        <>{renderDelete()}</>
      ) : (
        <>
          {isEditing ? (
            <Form onSubmit={(e: any) => onClickSaveFieldSubmit(e)}>
              <Row className="mb-2 g-3">
                <Col xs={6}>
                  <Textarea label="Label" value={fieldLabel} onUpdate={(e: any) => updateLabel(e)} rows={1} css={""} />
                </Col>
                <Col>
                  <SelectObject
                    label="Type"
                    value={fieldType}
                    options={typesList}
                    onUpdate={(e: any) => updateType(e)}
                    disabled={published ? true : false}
                  />
                </Col>
                <Col xs={"auto"} className="d-flex align-items-center">
                  <Button
                    type="submit"
                    className="button app-secondary-color me-3"
                    onClick={() => onClickSaveField()}
                    disabled={!allowSave}
                  >
                    Save
                  </Button>
                  <div className="vr my-2" />
                  <Button className="button app-primary-color ms-3" onClick={(e: any) => onClickCancel(e)}>
                    Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
          ) : (
            <Row className="mb-2 g-3">
              <Col xs={6}>
                <Textarea label="Label" value={fieldLabel} disabled={true} onUpdate={null} rows={1} css={""} />
              </Col>
              <Col>
                <Input label="Type" type="text" value={fieldType} disabled={true} onUpdate={null} />
              </Col>
              {!archived && !lastField && (
                <Col xs={"auto"} className="d-flex align-items-center">
                  <Stack direction="horizontal" gap={3} className="me-3">
                    {(field?.type === "dropdown" || field?.type === "text" || field?.type === "multi-line text") && (
                      <>
                        <IconButton
                          icon={previewEnabled ? faEye : faEyeSlash}
                          color={previewEnabled ? "app-green-text" : "text-muted"}
                          tooltipText={
                            !previewEnabled && numPreviews >= 2 ? "Max Selected" : previewEnabled ? "Disable Preview" : "Allow Preview"
                          }
                          disabled={!previewEnabled && numPreviews >= 2}
                          onClickButton={(e: any) => {
                            e.stopPropagation();
                            togglePreview(e);
                          }}
                        />
                        <div className="vr my-2" />
                      </>
                    )}
                    <IconButton onClickButton={(e: any) => onClickEdit(e)} tooltipText="Edit Field" icon={faEdit} />
                    {!published && (
                      <>
                        <div className="vr my-2" />
                        <IconButton onClickButton={() => onClickDelete()} tooltipText="Delete Field" icon={faTrash} />
                      </>
                    )}
                  </Stack>
                </Col>
              )}
            </Row>
          )}
          {field?.type === "dropdown" && field?.type === fieldType && (
            <FieldChoices
              noteTypeId={typeId}
              fieldId={field?.id}
              getNotes={getNotes}
              fieldChoices={options}
              archived={archived}
              published={published}
              setErrMessage={setErrMessage}
              setOrgToSave={setOrgToSave}
              setShowToast={setShowToast}
              setToastText={setToastText}
              setToastBody={setToastBody}
            />
          )}
        </>
      )}
    </>
  );
};

export default NoteTypeCardField;
